import { EllipsisVertical, Flag, Info, Trash2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useParams } from 'react-router'

import type { UpdateOrganizationTogglesBody } from '@ankor-io/common/types/system'
import { FeatureToggleName, FeatureToggleValue } from '@ankor-io/common/types/system'
import {
  Badge,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  EmptyState,
  Heading,
  Loading,
  Switch,
} from '@ankor-io/ui-components'

import { CreateFeatureFlagDialog } from '@/components/feature-flags/create-feature-flag-dialog'
import {
  useDeleteOrganizationToggleMutation,
  useOrganizationFeatureTogglesQuery,
  useUpdateOrganizationFeatureTogglesMutation,
} from '@/queries'
import { formatToggleName } from '@/utils/format'

interface OrganizationFeatureFlagsProps {
  isDialogOpen: boolean
  setIsDialogOpen: (open: boolean) => void
}

export function OrganizationFeatureFlags({ isDialogOpen, setIsDialogOpen }: OrganizationFeatureFlagsProps) {
  const { id } = useParams<{ id: string }>()
  const {
    data: organizationFeatureToggles,
    isPending,
    error,
  } = useOrganizationFeatureTogglesQuery(id, {
    enabled: !!id,
  })
  const { mutate: updateToggles, isPending: isUpdating } = useUpdateOrganizationFeatureTogglesMutation(id)
  const { mutate: deleteToggle, isPending: isDeleting } = useDeleteOrganizationToggleMutation(id)

  // Error toast is handled globally by QueryProvider

  if (!id) {
    return (
      <EmptyState
        heading="Organization ID missing"
        subheading="Unable to load feature toggles. Please navigate to a valid organization page."
        icon={<Flag className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  if (isPending) {
    return <Loading variant="spinner" />
  }
  if (error) {
    return (
      <EmptyState
        heading="Failed to load feature toggles"
        subheading={error.message || 'An error occurred while loading feature toggles.'}
        icon={<Flag className="mx-auto size-12 text-gray-400" />}
      />
    )
  }
  if (!organizationFeatureToggles) {
    return (
      <EmptyState
        heading="No feature toggles found"
        subheading="Get started by adding a new feature flag."
        icon={<Flag className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  const toggles = organizationFeatureToggles.toggles

  /**
   * Determines if the toggle switch should be active (checked)
   * Logic: Active when orgValue is enabled, OR when orgValue is null and globalDefault is enabled
   */
  const isToggleActive = (orgValue: FeatureToggleValue | null): boolean => {
    if (orgValue !== null) {
      return orgValue === FeatureToggleValue.ENABLED
    }
    return false
  }

  /**
   * Returns the global default badge color and label
   */
  const getGlobalDefaultBadge = (globalDefault: FeatureToggleValue): { label: string; color: 'green' | 'gray' } => {
    if (globalDefault === FeatureToggleValue.ENABLED) {
      return { label: 'Global Default: ON', color: 'green' }
    } else {
      return { label: 'Global Default: OFF', color: 'gray' }
    }
  }

  /**
   * Determines if override badge should be shown
   * Only show when orgValue differs from globalDefault
   */
  const shouldShowOverride = (globalDefault: FeatureToggleValue, orgValue: FeatureToggleValue | null): boolean => {
    if (orgValue === null) return false
    return orgValue !== globalDefault
  }

  /**
   * Handles toggle switch changes
   * Toggles the effective value (considering both orgValue and globalDefault)
   *
   * Logic:
   * - Calculate what the new effective value should be (opposite of current)
   * - Send that value to the backend
   * - Backend will set orgValue to that value if it differs from globalDefault, or null if it matches
   */
  const handleToggleChange = (toggleName: string, currentValue: boolean) => {
    // Calculate the opposite of the current effective value
    const newValue = currentValue ? FeatureToggleValue.DISABLED : FeatureToggleValue.ENABLED

    // Build request body with array of FeatureToggle objects as expected by the backend
    const body: UpdateOrganizationTogglesBody = {
      toggles: [
        {
          name: toggleName as FeatureToggleName,
          value: newValue,
        },
      ],
    }

    updateToggles(body, {
      onSuccess: () => {
        toast.success(`${formatToggleName(toggleName)} updated successfully`)
      },
      onError: (error) => {
        toast.error(`Failed to update ${formatToggleName(toggleName)}: ${error.message || 'An error occurred'}`)
      },
    })
  }

  /**
   * Handles creating a new feature toggle override for the organization
   */
  const handleCreateToggle = (toggleName: string, toggleValue: FeatureToggleValue) => {
    const body: UpdateOrganizationTogglesBody = {
      toggles: [
        {
          name: toggleName as FeatureToggleName,
          value: toggleValue,
        },
      ],
    }

    updateToggles(body, {
      onSuccess: () => {
        toast.success(`${formatToggleName(toggleName)} override created successfully`)
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error(
          `Failed to create ${formatToggleName(toggleName)} override: ${error.message || 'An error occurred'}`,
        )
      },
    })
  }

  /**
   * Handles deleting a feature toggle override for the organization
   * This reverts the toggle to the global default value
   */
  const handleDeleteToggle = (toggleName: string) => {
    deleteToggle(toggleName, {
      onSuccess: () => {
        toast.success(`${formatToggleName(toggleName)} override deleted successfully`)
      },
      onError: (error) => {
        toast.error(
          `Failed to delete ${formatToggleName(toggleName)} override: ${error.message || 'An error occurred'}`,
        )
      },
    })
  }

  // Filter toggles to only show those with organization overrides (orgValue !== null)
  const togglesWithOverrides = toggles ? Object.entries(toggles).filter(([, toggle]) => toggle.orgValue !== null) : []

  // Get list of toggle names that already have overrides (for disabling in dialog)
  const existingToggleNames = togglesWithOverrides.map(([name]) => name)

  return (
    <>
      {togglesWithOverrides.length === 0 ? (
        <EmptyState
          heading="No feature flags overrides"
          subheading="Get started by creating a new feature flag override for this organization."
          icon={<Flag className="mx-auto size-12 text-gray-400" />}
        />
      ) : (
        <div className="space-y-6">
          {/* Existing Feature Toggles */}
          {togglesWithOverrides.map(([name, toggle]) => {
            const isActive = isToggleActive(toggle.orgValue ?? null)
            const globalBadge = getGlobalDefaultBadge(toggle.globalDefault)
            const showOverride = shouldShowOverride(toggle.globalDefault, toggle.orgValue)

            return (
              <div
                key={name}
                className="flex items-center justify-between p-4 rounded-md border border-gray-200 dark:border-gray-700"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Heading level={5}>{formatToggleName(name)}</Heading>
                    <Badge
                      color={globalBadge.color}
                      className="text-xs w-fit"
                    >
                      {globalBadge.label}
                    </Badge>
                  </div>
                  {showOverride && (
                    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Info className="h-3 w-3" />
                      Override Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isActive}
                    disabled={isUpdating || isDeleting}
                    onChange={() => handleToggleChange(name, isActive)}
                  />

                  <div
                    className="relative z-10 pointer-events-auto"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Dropdown>
                      <DropdownButton plain>
                        <EllipsisVertical data-slot="icon" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem
                          onClick={() => handleDeleteToggle(name)}
                          disabled={isDeleting}
                        >
                          <Trash2 data-slot="icon" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">Delete</span>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <CreateFeatureFlagDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleCreateToggle}
        isSubmitting={isUpdating}
        title="Add Feature Flag Override"
        description="Create an organization-specific override for a feature flag. This will allow you to control the flag independently from the global default."
        buttonText="Create Override"
        existingToggleNames={existingToggleNames}
      />
    </>
  )
}
